/* ask_user's questions (backend/agent/tools/ask_user.py) as the dock asks them, and the
   person's picks as the words that answer them. */

export interface AskOption {
  label: string;
  description: string;
}

export interface AskQuestion {
  question: string;
  /** A short name for the question: its step on the dock's strip. */
  header: string;
  options: AskOption[];
  multi_select?: boolean;
}

/** What the person picked for one question: options by index, and their own words when they
    chose "Something else" (null while they haven't). */
export interface Pick {
  options: number[];
  own: string | null;
}

export const NO_PICK: Pick = { options: [], own: null };

const RECOMMENDED = / \(Recommended\)$/;

/** The agent recommends the option: it ends its label with "(Recommended)". */
export const isRecommended = (label: string) => RECOMMENDED.test(label);

/** The label as the person reads it, without the recommendation. */
export const plainLabel = (label: string) => label.replace(RECOMMENDED, "");

/** The questions an ask_user call asks; none for one that asked in words alone (a run paused
    before ask_user had options). Some models send the list as a JSON string. */
export function questionsOf(args: Record<string, unknown> | undefined): AskQuestion[] {
  let questions = args?.questions;
  if (typeof questions === "string") {
    try {
      questions = JSON.parse(questions);
    } catch {
      return [];
    }
  }
  return Array.isArray(questions) ? questions.filter(isQuestion) : [];
}

function isQuestion(value: unknown): value is AskQuestion {
  const q = value as AskQuestion;
  return typeof q?.question === "string" && Array.isArray(q.options) && q.options.every((o) => typeof o?.label === "string");
}

export const answered = (pick: Pick) => pick.options.length > 0 || !!pick.own?.trim();

/** One question's answer: the options picked, then the person's own words. */
export const pickText = (question: AskQuestion, pick: Pick) =>
  [...pick.options.map((i) => plainLabel(question.options[i].label)), pick.own?.trim()].filter(Boolean).join(", ");

/** The words that answer the call: the answer alone for one question, else each question's
    header and answer on a line ("Refunds: Ask me first"). */
export function answerText(questions: AskQuestion[], picks: Pick[]): string {
  if (questions.length === 1) return pickText(questions[0], picks[0]);
  return questions.map((q, i) => `${q.header || q.question}: ${pickText(q, picks[i])}`).join("\n");
}

/** Each question's answer read back from the words `answerText` made; null for words the person
    wrote themselves (in the composer, or on a channel), which answer all of them at once. */
export function answersIn(questions: AskQuestion[], text: string): string[] | null {
  if (questions.length === 1) return [text];
  const lines = text.split("\n");
  const answers = questions.map((q, i) => {
    const prefix = `${q.header || q.question}: `;
    return lines[i]?.startsWith(prefix) ? lines[i].slice(prefix.length) : null;
  });
  return lines.length === questions.length && answers.every((a) => a !== null) ? (answers as string[]) : null;
}
